import BlockRoundedIcon from '@mui/icons-material/BlockRounded';
import ChatRoundedIcon from '@mui/icons-material/ChatRounded';
import TelegramIcon from '@mui/icons-material/Telegram';
import { Chip } from '@mui/material';
import { useTranslation } from 'react-i18next';

import type { ConversationChannelLike } from './types';

export interface ChannelBadgeProps {
  channel: ConversationChannelLike;
  size?: 'small' | 'medium';
  variant?: 'filled' | 'outlined';
  /** Renders the blocked glyph instead of the channel's own icon — design §7.1/§7.3/§11.7. */
  blocked?: boolean;
  className?: string;
}

/** MAX has no dedicated brand glyph in Material Symbols — a generic chat bubble stands in for it. */
const CHANNEL_ICON: Record<ConversationChannelLike, typeof TelegramIcon> = {
  TELEGRAM: TelegramIcon,
  MAX: ChatRoundedIcon,
};

/**
 * The channel identity chip shared by the inbox row, the conversation header, the Sessions card, the
 * composer's channel selector and Customer 360 (design §2 shared primitives; `@/shared/ui/conversation`
 * because `features/customers` renders it too and FSD forbids importing another feature).
 */
export function ChannelBadge({
  channel,
  size = 'small',
  variant = 'filled',
  blocked = false,
  className,
}: ChannelBadgeProps) {
  const { t } = useTranslation('conversations');
  const Icon = blocked ? BlockRoundedIcon : CHANNEL_ICON[channel];
  const label = t(`channel.${channel}`, channel);
  return (
    <Chip
      className={className}
      size={size}
      variant={variant}
      color={blocked ? 'default' : 'primary'}
      icon={<Icon fontSize="inherit" />}
      label={label}
      // The visible label alone announces only the channel — a blocked session needs the state in its
      // OWN accessible name too ("Telegram — blocked the bot"), not just a sighted-only tooltip
      // (design §13.1/§13.8 — a shipped defect this fixes).
      aria-label={blocked ? t('channelBadge.blockedAria', { channel: label }) : undefined}
    />
  );
}
